import { AddCircle, CheckCircleOutline, Edit, History, Visibility } from '@mui/icons-material';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import { Box, Chip, FormControlLabel, Tooltip } from '@mui/material';
import MuiSwitch from '@mui/material/Switch';
import { GridActionsCellItem, GridSortModel } from '@mui/x-data-grid-premium';
import { useCallback, useContext, useEffect, useState } from 'react';
import { ITableDataPageConfigProps, TableDataPage } from 'shared/components';
import { MassActionToolbar } from 'shared/components/massActions';
import ReplaceText, { ReplaceTextForm } from 'shared/components/massActions/ReplaceText';
import { ApprovalsMassActionEnum } from 'shared/constants';
import { useApproverUsers, useUserPermissions } from 'shared/hooks';
import { useApprovalMassActionLambdaMutation } from 'shared/hooks/mutations/useApprovalMassActionLambdaMutation';
import { useRejectionMassActionLambdaMutation } from 'shared/hooks/mutations/useRejectionMassActionLambdaMutation';
import { useGetPriceHistory } from 'shared/hooks/useGetPriceHistory';
import { useAuthState } from 'shared/stores';
import { GridColumns, MenuOption } from 'shared/types';
import { toBackendFilterModel } from 'shared/utility/dataGrid';
import {
    determineApprovalLevel,
    formatDatePickerStringWithoutTime,
    formatUtcDateTimeToLocalDateTimeString,
    parseDatePickerDate,
    parseUtcDateTime,
} from 'shared/utility';
import { getStatusColumnColor, moneyFourDecimalFormatter } from 'shared/utility/dataGrid';
import { ApprovalDashboardData, PriceHistoryData } from 'types/backend';
import { PriceHistoryDialogProps, PriceHistoryModal } from '../../shared/components/priceHistoryModal';
import { ApprovalActions } from './approvalActions';
import { AnalyticsDrawer } from './approvalAnalyticsDrawer/AnalyticsDrawer';
import { useAnalyticsDrawer } from './approvalAnalyticsDrawer/useAnalyticsDrawer';
import { approvalsContext, defaultApprovalsConfig } from './approvalsResolver';
import { EditStatusForm } from './editStatusForm';

interface EditStatusDialogProps {
    open: boolean;
    row?: ApprovalDashboardData;
}

export function Approvals() {
    const approvalsContextValue = useContext(approvalsContext);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const [priceHistoryDialogState, setPriceHistoryDialogState] = useState<
        PriceHistoryDialogProps<ApprovalDashboardData>
    >({ open: false });
    const [editStatusDialogState, setEditStatusDialogState] = useState<EditStatusDialogProps>({
        open: false,
    });
    const [selectedApprovalDashboardData, setSelectedApprovalDashboardData] = useState<ApprovalDashboardData[]>([]);
    const [allRowsSelected, setAllRowsSelected] = useState<boolean>(false);
    const [selectedMassActionType, setSelectedMassActionType] = useState<string>('');
    const authState = useAuthState();
    const { data: approverUsers } = useApproverUsers();
    const { isMassEditMarketPricesAllowed, isMassEditDeviationsAllowed } = useUserPermissions();
    const { response, massActionModal, filtersApplied } = useContext(approvalsContext);
    const userDisplayName = authState.user.get()?.displayName ?? '';
    const [isRequestorOnly, setIsRequestorOnly] = useState<boolean>(false);

    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'startdate', sort: 'desc' }]);

    const handleSortModelChange = (newSortModel: GridSortModel) => {
        setSortModel(newSortModel);
    };

    const { analyticsDrawerRow, openAnalyticsDrawer, closeAnalyticsDrawer, isAnalyticsDrawerOpen } =
        useAnalyticsDrawer<ApprovalDashboardData>();

    const handleSuccessMassActionsSubmit = useCallback(() => {
        setSelectedApprovalDashboardData([]);
    }, [setSelectedApprovalDashboardData]);

    const handleMassActionSelected = (key: string) => {
        setSelectedMassActionType(key);
        massActionModal.handleMassActionModalOpen();
    };

    const handleAllRowsSelected = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setAllRowsSelected(event.target.checked);
            setSelectedApprovalDashboardData([]);
        },
        [setSelectedApprovalDashboardData]
    );

    const handleClick = (params: ApprovalDashboardData) => () => {
        if (selectedApprovalDashboardData.map((s) => s.id).some((s) => s === params.id)) {
            const newSelected = [...selectedApprovalDashboardData];
            newSelected.splice(
                selectedApprovalDashboardData.map((s) => s.id).findIndex((s) => s === params.id),
                1
            );
            setSelectedApprovalDashboardData([...newSelected]);
        } else {
            setSelectedApprovalDashboardData([...selectedApprovalDashboardData, params]);
        }
    };

    useEffect(() => {
        handleSuccessMassActionsSubmit();
        setAllRowsSelected(false);
    }, [filtersApplied, handleSuccessMassActionsSubmit, setSelectedApprovalDashboardData, setAllRowsSelected]);

    const submitRejectionMassActionMutation = useRejectionMassActionLambdaMutation({
        gridContext: approvalsContext,
        selectedRowIds: selectedApprovalDashboardData.map((s) => s.currentpriceid),
        isAllRowsSelected: allRowsSelected,
        onSuccess: handleSuccessMassActionsSubmit,
        isRequestorOnly,
    });

    const submitApprovalMassActionMutation = useApprovalMassActionLambdaMutation({
        gridContext: approvalsContext,
        selectedRowIds: selectedApprovalDashboardData.map((s) => s.currentpriceid),
        isAllRowsSelected: allRowsSelected,
        onSuccess: handleSuccessMassActionsSubmit,
        isRequestorOnly,
    });

    const tableConfig: ITableDataPageConfigProps = {
        pinnedColumnsProps: {
            pinAllColumns: true,
            pinnable: true,
            pinnedColumnsStorage: 'active-approvals-pinned-columns',
            pinnedColumnsDefault: ['edit'],
        },
    };

    useEffect(() => {
        if (isFirstRender && defaultApprovalsConfig) {
            const newTableDataRequest = {
                FilterModel: defaultApprovalsConfig.initialState?.filter?.filterModel
                    ? toBackendFilterModel(defaultApprovalsConfig.initialState.filter.filterModel)
                    : undefined,
                SortModel: defaultApprovalsConfig.initialState?.sorting?.sortModel,
                PageNumber: defaultApprovalsConfig.initialState?.pagination?.paginationModel?.page || 0,
                PageSize: defaultApprovalsConfig.initialState?.pagination?.paginationModel?.pageSize || 15,
            };
            if (
                JSON.stringify(approvalsContextValue.body.FilterModel) !==
                JSON.stringify(newTableDataRequest.FilterModel)
            ) {
                return approvalsContextValue.dontDebounceTableDataRequest(newTableDataRequest);
            }
            setIsFirstRender(false);
        }
    }, [approvalsContextValue, isFirstRender]);

    const handleSelectOwnerPrices = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (isRequestorOnly !== event.target.checked) {
                setIsRequestorOnly(event.target.checked);
            }
            if (defaultApprovalsConfig) {
                const newTableDataRequest = {
                    FilterModel: (defaultApprovalsConfig.initialState?.filter?.filterModel
                        ? toBackendFilterModel(defaultApprovalsConfig.initialState.filter.filterModel)
                        : undefined) || { items: [] },
                    SortModel: defaultApprovalsConfig.initialState?.sorting?.sortModel,
                    PageNumber: defaultApprovalsConfig.initialState?.pagination?.paginationModel?.page || 0,
                    PageSize: defaultApprovalsConfig.initialState?.pagination?.paginationModel?.pageSize || 15,
                };

                if (event.target.checked) {
                    newTableDataRequest.FilterModel?.items.push({
                        columnField: 'requestor',
                        operatorValue: 'equals',
                        value: userDisplayName,
                    });
                } else {
                    const indexRequestor = newTableDataRequest.FilterModel?.items.findIndex(
                        (i) => i.columnField === 'requestor'
                    );
                    if (indexRequestor) {
                        newTableDataRequest.FilterModel?.items.splice(indexRequestor, 1);
                    }
                }
                approvalsContextValue.dontDebounceTableDataRequest(newTableDataRequest);
            }
        },
        [approvalsContextValue, userDisplayName, isRequestorOnly]
    );

    const {
        response: priceHistoryResponse,
        serverSideProps: priceHistoryServerSideProps,
        setTableDataRequest,
    } = useGetPriceHistory(
        priceHistoryDialogState.row ? priceHistoryDialogState.row?.id : '',
        sortModel[0]?.field,
        sortModel[0]?.sort
    );

    useEffect(() => {
        if (sortModel.length > 0) {
            setTableDataRequest({ SortModel: sortModel });
        }
    }, [sortModel, setTableDataRequest]);

    const priceHistoryData: PriceHistoryData[] = priceHistoryResponse?.data?.rows || [];

    const openPriceHistoryDialog = useCallback(
        (price: ApprovalDashboardData) => {
            setPriceHistoryDialogState({
                open: true,
                row: price,
            });
        },
        [setPriceHistoryDialogState]
    );

    const closePriceHistoryDialog = () => {
        setPriceHistoryDialogState({
            open: false,
            row: undefined,
        });
    };

    const openEditStatusDialog = (row: ApprovalDashboardData) => {
        setEditStatusDialogState({
            open: true,
            row: row,
        });
    };

    const closeEditStatusDialog = () => {
        setEditStatusDialogState({
            open: false,
            row: undefined,
        });
    };

    const isSelectedIcon = useCallback(
        (row: ApprovalDashboardData) =>
            allRowsSelected
                ? !selectedApprovalDashboardData.some((s) => row.id === s.id)
                : selectedApprovalDashboardData.some((s) => row.id === s.id),
        [selectedApprovalDashboardData, allRowsSelected]
    );

    const isApprover = (() => {
        const approverUser = approverUsers?.filter((x) => {
            return x.name.toLowerCase() === userDisplayName.toLowerCase();
        });
        if (approverUser?.length === 0) return false;
        if (approverUser?.[0]?.islevel1approver) return true;
        if (approverUser?.[0]?.islevel2approver) return true;
        return false;
    })();

    const isMassActionApprover = isApprover && isMassEditMarketPricesAllowed && isMassEditDeviationsAllowed;

    const columnConfig: GridColumns<ApprovalDashboardData> = [
        {
            headerName: 'id',
            field: 'id',
            hide: true,
            filterable: false,
            pinnable: false,
            width: 200,
        },
        {
            field: 'edit',
            type: 'actions',
            hide: false,
            filterable: false,
            pinnable: false,
            width: 80,
            getActions: (params) => {
                return [
                    <GridActionsCellItem
                        icon={<Edit />}
                        label='Edit Status'
                        onClick={() => openEditStatusDialog(params.row)}
                        disabled={(() => {
                            if (params.row.status.toLowerCase() !== 'needs approval') return true;
                            const approverUser = approverUsers?.filter((x) => {
                                return x.name.toLowerCase() === userDisplayName.toLowerCase();
                            });
                            if (approverUser?.length === 0) return true;
                            const approvalLevel = determineApprovalLevel(approverUsers, params.row.approver);
                            if (approvalLevel === 'Level 1 Approver' && approverUser?.[0]?.islevel1approver)
                                return false;
                            if (approvalLevel === 'Level 2 Approver' && approverUser?.[0]?.islevel2approver)
                                return false;
                            return true;
                        })()}
                    />,
                ];
            },
        },
        {
            headerName: allRowsSelected ? 'Deselect Products' : 'Select Products',
            field: 'select',
            type: 'actions',
            hide: !isMassActionApprover,
            filterable: false,
            pinnable: false,
            width: 200,
            getActions: (params) => {
                return [
                    ...(isMassActionApprover
                        ? [
                              selectedApprovalDashboardData.map((s) => s.id).includes(params.row.id) ? (
                                  <GridActionsCellItem
                                      key={`selected-${params.row.id}`}
                                      icon={isSelectedIcon(params.row) ? <CheckCircleOutline /> : <AddCircle />}
                                      label='Selected'
                                      onClick={handleClick(params.row)}
                                      disabled={!isMassActionApprover}
                                  />
                              ) : (
                                  <GridActionsCellItem
                                      key={`select-${params.row.id}`}
                                      icon={isSelectedIcon(params.row) ? <CheckCircleOutline /> : <AddCircle />}
                                      label='Select'
                                      onClick={handleClick(params.row)}
                                      disabled={!isMassActionApprover}
                                  />
                              ),
                          ]
                        : []),
                ];
            },
        },
        {
            headerName: 'Requestor',
            field: 'requestor',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Date Requested',
            field: 'dateRequested',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            type: 'dateTime',
            width: 200,
        },
        {
            headerName: 'Customer Name',
            field: 'customerName',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Customer Number',
            field: 'customerNumber',
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Tier',
            field: 'tier',
            hideable: false,
            pinnable: false,
            width: 100,
        },
        {
            headerName: 'Pricing Owner',
            field: 'pricingOwner',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Territory Sales Leader',
            field: 'territorySalesLeader',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Price Type',
            field: 'priceType',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Product',
            field: 'productId',
            hideable: false,
            pinnable: false,
            width: 200,
        },
        {
            headerName: 'Status',
            field: 'status',
            hideable: false,
            pinnable: false,
            width: 150,
            renderCell: (params) =>
                params.row.status ? (
                    <Chip
                        label={params.row.status}
                        color={getStatusColumnColor(params.row.status)}
                        variant='outlined'
                    />
                ) : null,
        },
        {
            headerName: 'Approval Level',
            field: 'approvalLevel',
            hideable: false,
            pinnable: false,
            width: 150,
            renderCell: (params) => {
                return (
                    <Tooltip title={params.row.approver}>
                        <span>{params.value}</span>
                    </Tooltip>
                );
            },
        },
        {
            headerName: 'End User',
            field: 'endUser',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'End User Number',
            field: 'endUserNumber',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Price Group',
            field: 'priceGroup',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'OTO',
            field: 'isOneTimeOnly',
            hideable: false,
            pinnable: false,
            type: 'boolean',
            width: 150,
            renderCell: (params) => (params.row.priceType === 'Deviation' ? (params.value ? 'Y' : 'N') : ''),
        },
        {
            headerName: 'Reason',
            field: 'reason',
            hideable: false,
            pinnable: false,
            width: 150,
        },
        {
            headerName: 'Current Price',
            field: 'currentPrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 200,
        },
        {
            headerName: 'Requested Price',
            field: 'requestedPrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 200,
        },
        {
            headerName: 'Target Price',
            field: 'targetPrice',
            hideable: false,
            pinnable: false,
            type: 'number',
            valueFormatter: moneyFourDecimalFormatter,
            width: 200,
        },
        {
            headerName: 'End Date',
            field: 'endDateLocalTime',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseDatePickerDate(params.value),
            valueFormatter: (params) => formatDatePickerStringWithoutTime(params.value),
            type: 'date',
            filterable: true,
            width: 200,
        },
        {
            headerName: 'Last Updated',
            field: 'effectivestart',
            hideable: false,
            pinnable: false,
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            type: 'dateTime',
            width: 200,
        },
        {
            headerName: 'Date Approved',
            field: 'dateApproved',
            hideable: false,
            pinnable: false,
            type: 'dateTime',
            valueGetter: (params) => parseUtcDateTime(params.value),
            valueFormatter: (params) => formatUtcDateTimeToLocalDateTimeString(params.value),
            width: 200,
        },
        {
            headerName: 'Comments',
            field: 'comments',
            hideable: false,
            pinnable: false,
            align: 'center',
            headerAlign: 'center',
            width: 120,
            renderCell: (params) =>
                params.value ? (
                    <Tooltip title={<span style={{ fontSize: '.85rem' }}>{params.value}</span>}>
                        <Visibility />
                    </Tooltip>
                ) : (
                    <Visibility style={{ color: '#CCC' }} />
                ),
        },
        {
            headerName: 'Price History',
            field: 'actions',
            type: 'actions',
            width: 150,
            getActions: (params) => {
                const actions = [
                    <GridActionsCellItem
                        icon={<History />}
                        label='Price History'
                        onClick={() => openPriceHistoryDialog(params.row)}
                    />,
                ];

                if (isApprover)
                    return [
                        ...actions,
                        <GridActionsCellItem
                            icon={<EqualizerIcon />}
                            label='Analytics'
                            onClick={openAnalyticsDrawer(params.row)}
                            disabled={!isApprover}
                        />,
                    ];

                return actions;
            },
        },
    ];

    const menuOptions: MenuOption[] = [
        {
            key: ApprovalsMassActionEnum.APPROVE,
            value: 'Approve',
            action: handleMassActionSelected,
            disabled: false,
        },
        {
            key: ApprovalsMassActionEnum.REJECT,
            value: 'Reject',
            action: handleMassActionSelected,
            disabled: false,
        },
    ];

    const customToolbarChildren = [
        <FormControlLabel
            key={'viewItems'}
            control={<MuiSwitch onChange={handleSelectOwnerPrices} />}
            label='View my items only'
        />,
        isMassActionApprover
            ? [
                  <MassActionToolbar
                      key={'approval-mass-actions'}
                      menuOptions={menuOptions}
                      title={'Mass actions'}
                      disabled={!isMassActionApprover}
                      allowSelectAll={true}
                      switchContainerStyles={{ flex: undefined }}
                      disabledSelectAllButton={response?.data?.rows.length === 0}
                      handleAllRowsSelected={handleAllRowsSelected}
                      selectedMassActionType={selectedMassActionType}
                      handleOpen={massActionModal.handleMassActionModalOpen}
                      handleClose={massActionModal.handleMassActionModalClose}
                      isOpen={massActionModal.isMassActionModalopen}
                  >
                      {selectedMassActionType === ApprovalsMassActionEnum.REJECT && (
                          <ReplaceText
                              defaultValues={{
                                  type: selectedMassActionType,
                                  actionForm: { newText: '' },
                                  accountNumber: '',
                              }}
                              onSubmit={(submission: ReplaceTextForm) => {
                                  submitRejectionMassActionMutation.mutate(submission.actionForm.newText);
                              }}
                              isLoading={submitRejectionMassActionMutation.isLoading}
                              allRowsSelected={allRowsSelected}
                              tableDataContext={approvalsContext}
                              columnConfig={columnConfig}
                              rowCount={selectedApprovalDashboardData.length}
                              maxLength={2000}
                          />
                      )}
                      {selectedMassActionType === ApprovalsMassActionEnum.APPROVE && (
                          <ReplaceText
                              defaultValues={{
                                  type: selectedMassActionType,
                                  actionForm: { newText: '' },
                                  accountNumber: '',
                              }}
                              onSubmit={(submission: ReplaceTextForm) => {
                                  submitApprovalMassActionMutation.mutate(submission.actionForm.newText);
                              }}
                              isLoading={submitApprovalMassActionMutation.isLoading}
                              allRowsSelected={allRowsSelected}
                              tableDataContext={approvalsContext}
                              columnConfig={columnConfig}
                              rowCount={selectedApprovalDashboardData.length}
                              maxLength={2000}
                          />
                      )}
                  </MassActionToolbar>,
              ]
            : [],
        <Box key={'toolbar-flex-grow'} flexGrow={1} />,
        <ApprovalActions key='approvalActions' />,
    ];

    return (
        <>
            <TableDataPage
                title='Approvals Dashboard'
                columnConfig={columnConfig}
                tableDataContext={approvalsContext}
                customToolbarChildren={customToolbarChildren}
                config={tableConfig}
            />
            <PriceHistoryModal
                key={
                    priceHistoryDialogState.row
                        ? `${priceHistoryDialogState?.row?.customerNumber}-${priceHistoryDialogState?.row?.productId}-${priceHistoryDialogState?.row?.priceType}`
                        : 'empty'
                }
                open={priceHistoryDialogState.open}
                rows={priceHistoryData}
                totalRows={priceHistoryResponse.data?.totalRows || 0}
                onClose={closePriceHistoryDialog}
                isLoading={priceHistoryResponse.isLoading}
                isFetching={priceHistoryResponse.isFetching}
                serverSideProps={priceHistoryServerSideProps}
                sortModel={sortModel}
                onSortModelChange={handleSortModelChange}
            />
            <EditStatusForm
                open={editStatusDialogState.open}
                row={editStatusDialogState.row}
                onClose={closeEditStatusDialog}
            />
            {isApprover && (
                <AnalyticsDrawer
                    isAnalyticsDrawerOpen={isAnalyticsDrawerOpen}
                    closeAnalyticsDrawer={closeAnalyticsDrawer}
                    analyticsDrawerRow={analyticsDrawerRow}
                />
            )}
        </>
    );
}
